import React, { useState, useEffect, useCallback, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import EntityRelationshipGraph from './EntityRelationshipGraph';

interface EntityDetailDrawerProps {
  entity: any;
  open: boolean;
  onClose: () => void;
  onEntitySelect: (entity: any) => void;
  onGeneratePackage?: (entityId: string) => void;
  onCreateTasking?: (entityId: string) => void;
  onNavigateToLocation?: (lat: number, lon: number, label?: string) => void;
  activeBrief?: any;
}

const getAuthToken = (): string | null =>
  localStorage.getItem('auth_token') || localStorage.getItem('token') || localStorage.getItem('access_token');

const TYPE_COLORS: Record<string, string> = {
  VESSEL: 'bg-blue-600',
  AIRCRAFT: 'bg-cyan-600',
  PERSON: 'bg-orange-500',
  ORGANIZATION: 'bg-purple-600',
  LOCATION: 'bg-green-600',
  EVENT: 'bg-red-600',
  ASSET: 'bg-yellow-600',
};

const TLP_COLORS: Record<string, string> = {
  WHITE: 'bg-white text-black',
  GREEN: 'bg-green-600 text-white',
  AMBER: 'bg-amber-500 text-black',
  RED: 'bg-red-600 text-white',
};

const MODULE_COLORS: Record<string, string> = {
  GEOINT: 'text-emerald-400',
  MARITIME: 'text-blue-400',
  AIR: 'text-cyan-400',
  TELESCOPE: 'text-purple-400',
  X_INVESTIGATION: 'text-slate-400',
  BREACHINT: 'text-red-400',
  MONITOR: 'text-amber-400',
  SOCINT: 'text-pink-400',
};

const EntityDetailDrawer: React.FC<EntityDetailDrawerProps> = ({
  entity,
  open,
  onClose,
  onEntitySelect,
  onGeneratePackage,
  onCreateTasking,
  onNavigateToLocation,
  activeBrief,
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'graph' | 'timeline' | 'evidence' | 'aircraft'>('overview');
  const [timeline, setTimeline] = useState<any[]>([]);
  const [timelineTotal, setTimelineTotal] = useState(0);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [evidenceCrops, setEvidenceCrops] = useState<any[]>([]);
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [aircraftIntel, setAircraftIntel] = useState<any | null>(null);
  const [aircraftIntelLoading, setAircraftIntelLoading] = useState(false);
  const [isWatchlisted, setIsWatchlisted] = useState(false);
  const [analystNotes, setAnalystNotes] = useState('');
  const miniMapContainer = useRef<HTMLDivElement>(null);
  const miniMap = useRef<maplibregl.Map | null>(null);

  useEffect(() => {
    if (entity) {
      setIsWatchlisted(entity.is_watchlisted || false);
      setAnalystNotes(entity.attributes?.analyst_notes || '');
    }
  }, [entity]);

  // Fetch aircraft intel when tab active (for AIRCRAFT entities with a real ICAO24)
  useEffect(() => {
    const rawIcao = entity?.attributes?.icao24 || entity?.attributes?.icao;
    // Only use entity.name as ICAO if it's exactly 6 hex chars (e.g. "4ca9bb")
    const icao = rawIcao || (/^[0-9a-f]{6}$/i.test(entity?.name || '') ? entity.name : null);
    if (!icao || activeTab !== 'aircraft') return;
    setAircraftIntelLoading(true);
    setAircraftIntel(null);
    const token = getAuthToken();
    fetch(`/api/intel/aircraft/${icao}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setAircraftIntel(d); })
      .catch(() => {})
      .finally(() => setAircraftIntelLoading(false));
  }, [entity?.entity_id, entity?.attributes?.icao24, activeTab]);

  // Fetch timeline when tab active
  useEffect(() => {
    if (!entity?.entity_id || activeTab !== 'timeline') return;

    setTimelineLoading(true);
    const token = getAuthToken();
    fetch(`/api/entities/${entity.entity_id}/timeline?size=50`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setTimeline(data.entries || []);
          setTimelineTotal(data.total || 0);
        }
        setTimelineLoading(false);
      })
      .catch(() => setTimelineLoading(false));
  }, [entity?.entity_id, activeTab]);

  // Fetch imagery evidence for GEOINT entities
  useEffect(() => {
    if (!entity?.entity_id || activeTab !== 'evidence') return;
    setEvidenceLoading(true);
    const token = getAuthToken();
    fetch(`/api/geoint/detect/crops/${entity.entity_id}?limit=30`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        setEvidenceCrops(data?.crops || []);
        setEvidenceLoading(false);
      })
      .catch(() => setEvidenceLoading(false));
  }, [entity?.entity_id, activeTab]);

  const toggleWatchlist = useCallback(async () => {
    if (!entity?.entity_id) return;
    const token = getAuthToken();
    const method = isWatchlisted ? 'DELETE' : 'POST';
    try {
      const resp = await fetch(`/api/entities/${entity.entity_id}/watchlist`, {
        method,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (resp.ok) setIsWatchlisted(!isWatchlisted);
    } catch { /* ignore */ }
  }, [entity?.entity_id, isWatchlisted]);

  // Mini-map for entity location
  useEffect(() => {
    if (!entity?.last_location || !miniMapContainer.current || activeTab !== 'overview') return;
    // Small delay to let DOM render the container
    const timer = setTimeout(() => {
      if (!miniMapContainer.current) return;
      if (miniMap.current) { miniMap.current.remove(); miniMap.current = null; }

      const { lat, lon } = entity.last_location;
      const m = new maplibregl.Map({
        container: miniMapContainer.current!,
        style: { version: 8, sources: { 'esri-sat': { type: 'raster', tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'], tileSize: 256 } }, layers: [{ id: 'esri-sat', type: 'raster', source: 'esri-sat' }] },
        center: [lon, lat],
        zoom: 13,
        interactive: true,
        attributionControl: false,
      });
      m.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');

      m.on('load', () => {
        // Entity marker
        const el = document.createElement('div');
        el.style.cssText = 'width:14px;height:14px;border-radius:50%;border:2px solid #fff;box-shadow:0 0 6px rgba(0,0,0,.5);';
        const typeColors: Record<string, string> = { VESSEL: '#3b82f6', AIRCRAFT: '#06b6d4', PERSON: '#f97316', ORGANIZATION: '#a855f7', LOCATION: '#22c55e', EVENT: '#ef4444', ASSET: '#eab308' };
        el.style.backgroundColor = typeColors[entity.entity_type] || '#6b7280';
        new maplibregl.Marker({ element: el }).setLngLat([lon, lat]).addTo(m);

        // Location history track
        const hist = entity.location_history || [];
        if (hist.length > 1) {
          const coords = hist.map((h: any) => [h.lon, h.lat]).filter((c: number[]) => c[0] && c[1]);
          if (coords.length > 1) {
            m.addSource('track', { type: 'geojson', data: { type: 'Feature', geometry: { type: 'LineString', coordinates: coords }, properties: {} } });
            m.addLayer({ id: 'track-line', type: 'line', source: 'track', paint: { 'line-color': '#06b6d4', 'line-width': 2, 'line-dasharray': [3, 2] } });
            // Markers for each history point
            coords.forEach((c: number[], i: number) => {
              const dot = document.createElement('div');
              dot.style.cssText = `width:6px;height:6px;border-radius:50%;background:${i === coords.length - 1 ? '#22c55e' : '#94a3b8'};border:1px solid #fff;`;
              new maplibregl.Marker({ element: dot }).setLngLat(c as [number, number]).addTo(m);
            });
          }
        }
      });

      miniMap.current = m;
    }, 100);
    return () => {
      clearTimeout(timer);
      if (miniMap.current) { miniMap.current.remove(); miniMap.current = null; }
    };
  }, [entity?.entity_id, entity?.last_location, activeTab]);

  const saveNotes = useCallback(async () => {
    if (!entity?.entity_id) return;
    const token = getAuthToken();
    try {
      await fetch(`/api/entities/${entity.entity_id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          attributes: { ...entity.attributes, analyst_notes: analystNotes },
        }),
      });
    } catch { /* ignore */ }
  }, [entity, analystNotes]);

  const formatDate = (ts: string) => {
    if (!ts) return 'N/A';
    return new Date(ts).toLocaleString();
  };

  if (!open || !entity) return null;

  const entityType = entity.entity_type || 'UNKNOWN';
  const priority = entity.priority || 3;
  const tlp = entity.tlp || 'WHITE';

  return (
    <div className="fixed inset-y-0 right-0 w-[480px] bg-card border-l border-border shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-200">
      {/* Header */}
      <div className="p-4 border-b border-border shrink-0">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-bold text-foreground truncate">{entity.name}</h2>
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded text-white ${TYPE_COLORS[entityType] || 'bg-gray-600'}`}>
                {entityType}
              </span>
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${TLP_COLORS[tlp] || 'bg-gray-600 text-white'}`}>
                TLP:{tlp}
              </span>
            </div>
            {entity.aliases?.length > 0 && (
              <div className="text-xs text-muted-foreground mt-1">
                AKA: {entity.aliases.join(', ')}
              </div>
            )}
            <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
              <span>ID: {entity.entity_id?.slice(0, 8)}...</span>
              <span>Obs: {entity.observation_count || 0}</span>
              <span>Confidence: {((entity.confidence || 0) * 100).toFixed(0)}%</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground text-xl px-2"
          >
            x
          </button>
        </div>

        {/* Priority & Watchlist */}
        <div className="flex items-center gap-3 mt-3">
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-muted-foreground">Priority:</span>
            {[1, 2, 3, 4, 5].map(p => (
              <span
                key={p}
                className={`text-sm ${p <= priority ? 'text-amber-400' : 'text-muted'}`}
              >
                *
              </span>
            ))}
          </div>
          <button
            onClick={toggleWatchlist}
            className={`text-xs px-2 py-1 rounded transition-colors ${
              isWatchlisted
                ? 'bg-amber-600 text-white'
                : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            {isWatchlisted ? 'Watchlisted' : '+ Watchlist'}
          </button>
        </div>

        {/* Tab switcher */}
        <div className="flex gap-1 mt-3 flex-wrap">
          {(['overview', 'graph', 'timeline'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors capitalize ${
                activeTab === tab
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab}
            </button>
          ))}
          {entity?.contributing_modules?.includes('GEOINT') && (
            <button
              onClick={() => setActiveTab('evidence')}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                activeTab === 'evidence'
                  ? 'bg-emerald-700 text-white'
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              Evidence
            </button>
          )}
          {(entity?.type === 'AIRCRAFT' || entity?.entity_type === 'AIRCRAFT' || entity?.class_name === 'aircraft') && (
            <button
              onClick={() => setActiveTab('aircraft')}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                activeTab === 'aircraft'
                  ? 'bg-cyan-700 text-white'
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              Aircraft Intel
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'overview' && (
          <div className="p-4 space-y-4">
            {/* Description */}
            {entity.description && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Description</h4>
                <p className="text-sm text-foreground">{entity.description}</p>
              </div>
            )}

            {/* Attributes */}
            {entity.attributes && Object.keys(entity.attributes).length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Attributes</h4>
                <div className="space-y-1">
                  {Object.entries(entity.attributes)
                    .filter(([k]) => k !== 'analyst_notes')
                    .map(([key, val]) => (
                      <div key={key} className="flex justify-between text-xs py-1 border-b border-border/50">
                        <span className="text-muted-foreground">{key.replace(/_/g, ' ')}</span>
                        <span className="text-foreground font-medium truncate max-w-[200px]">
                          {typeof val === 'object' ? JSON.stringify(val) : String(val)}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Resolution keys */}
            {entity.resolution_keys && Object.keys(entity.resolution_keys).length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Identifiers</h4>
                <div className="space-y-1">
                  {Object.entries(entity.resolution_keys).map(([key, val]) => (
                    <div key={key} className="flex justify-between text-xs py-1 border-b border-border/50">
                      <span className="text-muted-foreground">{key}</span>
                      <span className="text-cyan-400 font-mono">{String(val)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Contributing modules */}
            {entity.contributing_modules?.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Contributing Modules</h4>
                <div className="flex flex-wrap gap-1">
                  {entity.contributing_modules.map((mod: string) => (
                    <span key={mod} className={`text-[10px] font-medium ${MODULE_COLORS[mod] || 'text-foreground'}`}>
                      {mod}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Location with mini-map */}
            {entity.last_location && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Last Known Location</h4>
                <div className="text-sm text-foreground mb-2">
                  {entity.last_location.lat?.toFixed(6)}, {entity.last_location.lon?.toFixed(6)}
                  {entity.location_history?.length > 0 && (
                    <span className="text-[10px] text-muted-foreground ml-2">
                      ({entity.location_history.length} location(s) in history)
                    </span>
                  )}
                </div>
                <div
                  ref={miniMapContainer}
                  className="w-full h-[200px] rounded-md border border-border overflow-hidden"
                />
              </div>
            )}

            {/* Timestamps */}
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Timestamps</h4>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">First seen</span>
                  <span className="text-foreground">{formatDate(entity.first_seen)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Last seen</span>
                  <span className="text-foreground">{formatDate(entity.last_seen)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Created</span>
                  <span className="text-foreground">{formatDate(entity.created_at)}</span>
                </div>
              </div>
            </div>

            {/* Tags */}
            {entity.tags?.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Tags</h4>
                <div className="flex flex-wrap gap-1">
                  {entity.tags.map((tag: string) => (
                    <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-foreground border border-border">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Analyst Notes */}
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Analyst Notes</h4>
              <textarea
                value={analystNotes}
                onChange={(e) => setAnalystNotes(e.target.value)}
                placeholder="Add analyst notes..."
                className="w-full h-24 px-3 py-2 bg-background border border-border rounded-md text-foreground text-xs placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary resize-none"
              />
              <button
                onClick={saveNotes}
                className="mt-1 px-3 py-1 text-[10px] font-medium rounded bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Save Notes
              </button>
            </div>
          </div>
        )}

        {activeTab === 'graph' && (
          <div className="p-4">
            <EntityRelationshipGraph
              entityId={entity.entity_id}
              height={400}
              onNodeClick={onEntitySelect}
            />
          </div>
        )}

        {activeTab === 'timeline' && (
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Event Timeline
              </h4>
              <span className="text-[10px] text-muted-foreground">{timelineTotal} events</span>
            </div>

            {timelineLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin h-4 w-4 border-2 border-cyan-400 border-t-transparent rounded-full" />
              </div>
            ) : timeline.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">No timeline events</p>
            ) : (
              <div className="relative">
                {/* Vertical line */}
                <div className="absolute left-3 top-0 bottom-0 w-px bg-border" />

                <div className="space-y-3">
                  {timeline.map((entry: any, i: number) => {
                    const module = entry.source_module || 'UNKNOWN';
                    const hasLocation = entry.location?.lat != null && entry.location?.lon != null;
                    return (
                      <div
                        key={i}
                        className={`relative pl-8 ${hasLocation ? 'cursor-pointer hover:bg-primary/5 -mx-2 px-10 py-1 rounded transition-colors' : ''}`}
                        onClick={() => {
                          if (hasLocation && onNavigateToLocation) {
                            onNavigateToLocation(entry.location.lat, entry.location.lon, entry.name);
                          }
                        }}
                        title={hasLocation ? 'Click to navigate to this location on the map' : undefined}
                      >
                        {/* Dot */}
                        <div
                          className="absolute left-1.5 top-1 w-3 h-3 rounded-full border-2 border-card"
                          style={{
                            backgroundColor:
                              module === 'GEOINT' ? '#10b981' :
                              module === 'MARITIME' ? '#3b82f6' :
                              module === 'AIR' ? '#06b6d4' :
                              module === 'TELESCOPE' ? '#a855f7' :
                              module === 'X_INVESTIGATION' ? '#64748b' :
                              module === 'BREACHINT' ? '#ef4444' :
                              '#6b7280',
                          }}
                        />
                        <div className="text-xs">
                          <div className="flex items-center gap-2">
                            <span className={`font-medium ${MODULE_COLORS[module] || 'text-foreground'}`}>
                              {module}
                            </span>
                            <span className="text-[10px] text-muted-foreground">
                              {formatDate(entry.timestamp)}
                            </span>
                          </div>
                          <div className="text-foreground mt-0.5">{entry.name}</div>
                          {hasLocation && (
                            <div className="text-[10px] text-cyan-400 mt-0.5 flex items-center gap-1">
                              <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                              {entry.location.lat?.toFixed(4)}, {entry.location.lon?.toFixed(4)}
                            </div>
                          )}
                          {entry.confidence != null && (
                            <div className="text-[10px] text-muted-foreground">
                              Confidence: {(entry.confidence * 100).toFixed(0)}%
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'evidence' && (
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Satellite Imagery Evidence
              </h4>
              {evidenceCrops.length > 0 && (
                <span className="text-[10px] text-muted-foreground">{evidenceCrops.length} detections</span>
              )}
            </div>

            {evidenceLoading && (
              <div className="flex items-center gap-2 py-6 justify-center">
                <div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                <span className="text-xs text-muted-foreground">Loading imagery evidence...</span>
              </div>
            )}

            {!evidenceLoading && evidenceCrops.length === 0 && (() => {
              // Try to find matching detections from the active Intel brief
              const entityLat = entity?.attributes?.latitude ?? entity?.lat;
              const entityLon = entity?.attributes?.longitude ?? entity?.lon;
              const briefDetections: any[] = activeBrief?.satellite?.detections || [];
              const nearbyDetections = entityLat && entityLon
                ? briefDetections.filter((d: any) => {
                    if (!d.lat || !d.lon) return false;
                    const dlat = (d.lat - entityLat) * 111;
                    const dlon = (d.lon - entityLon) * 111 * Math.cos(entityLat * Math.PI / 180);
                    return Math.sqrt(dlat * dlat + dlon * dlon) < 0.5; // within 500m
                  })
                : [];
              if (nearbyDetections.length > 0) {
                return (
                  <div className="space-y-4">
                    <p className="text-[10px] text-muted-foreground">From active Intel collection ({nearbyDetections.length} detection{nearbyDetections.length > 1 ? 's' : ''} within 500m)</p>
                    {nearbyDetections.map((d: any, i: number) => (
                      <div key={i} className="rounded-lg border border-border overflow-hidden">
                        {d.crop_b64 && (
                          <div className="relative bg-black">
                            <img src={`data:image/jpeg;base64,${d.crop_b64}`} alt={`Detection ${i + 1}`}
                              className="w-full object-contain max-h-48" style={{ imageRendering: 'pixelated' }} />
                            <div className="absolute top-2 left-2">
                              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-600/90 text-white uppercase tracking-wider">{d.class_name}</span>
                            </div>
                            <div className="absolute top-2 right-2">
                              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-black/70 text-emerald-400">{((d.confidence || 0) * 100).toFixed(0)}%</span>
                            </div>
                          </div>
                        )}
                        <div className="p-3 space-y-1 bg-muted/30 text-[10px]">
                          <div className="flex justify-between"><span className="text-muted-foreground">Location</span><span className="text-cyan-400 font-mono">{d.lat?.toFixed(5)}, {d.lon?.toFixed(5)}</span></div>
                          <div className="flex justify-between"><span className="text-muted-foreground">Scene</span><span className="text-foreground font-mono truncate max-w-[140px]">{d.scene_id || '—'}</span></div>
                          <div className="flex justify-between"><span className="text-muted-foreground">Model</span><span className="text-foreground truncate max-w-[140px]">{d.model || '—'}</span></div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              }
              return (
                <div className="text-center py-8">
                  <div className="text-muted-foreground text-xs">No imagery evidence stored yet.</div>
                  <div className="text-muted-foreground text-[10px] mt-1">
                    Run "Collect Intelligence" on the Intel tab to capture satellite evidence for this area.
                  </div>
                </div>
              );
            })()}

            {!evidenceLoading && evidenceCrops.length > 0 && (
              <div className="space-y-4">
                {evidenceCrops.map((crop: any, i: number) => (
                  <div key={i} className="rounded-lg border border-border overflow-hidden">
                    {/* Satellite crop image */}
                    <div className="relative bg-black">
                      <img
                        src={`data:image/jpeg;base64,${crop.crop_b64}`}
                        alt={`Detection ${i + 1}`}
                        className="w-full object-contain max-h-48"
                        style={{ imageRendering: 'pixelated' }}
                      />
                      {/* Class badge overlay */}
                      <div className="absolute top-2 left-2">
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-600/90 text-white uppercase tracking-wider">
                          {crop.class_name}
                        </span>
                      </div>
                      {/* Confidence overlay */}
                      <div className="absolute top-2 right-2">
                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-black/70 text-emerald-400">
                          {((crop.confidence || 0) * 100).toFixed(0)}%
                        </span>
                      </div>
                    </div>

                    {/* Metadata */}
                    <div className="p-3 space-y-1.5 bg-muted/30">
                      <div className="flex justify-between text-[10px]">
                        <span className="text-muted-foreground">Detected</span>
                        <span className="text-foreground font-mono">
                          {crop.detected_at ? new Date(crop.detected_at).toLocaleString() : '—'}
                        </span>
                      </div>
                      <div className="flex justify-between text-[10px]">
                        <span className="text-muted-foreground">Location</span>
                        <span className="text-cyan-400 font-mono">
                          {crop.lat?.toFixed(5)}, {crop.lon?.toFixed(5)}
                        </span>
                      </div>
                      {crop.aoi_name && (
                        <div className="flex justify-between text-[10px]">
                          <span className="text-muted-foreground">AOI</span>
                          <span className="text-foreground">{crop.aoi_name}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-[10px]">
                        <span className="text-muted-foreground">Scene</span>
                        <span className="text-foreground font-mono truncate max-w-[140px]">{crop.scene_id || '—'}</span>
                      </div>
                      <div className="flex justify-between text-[10px]">
                        <span className="text-muted-foreground">Model</span>
                        <span className="text-foreground truncate max-w-[140px]">{crop.model || '—'}</span>
                      </div>
                      {crop.tile_url && (
                        <a
                          href={crop.tile_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block text-[10px] text-blue-400 hover:text-blue-300 truncate"
                        >
                          View source tile →
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'aircraft' && (
          <div className="p-4 space-y-4">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Aircraft Intelligence</h4>

            {aircraftIntelLoading && (
              <div className="flex items-center gap-2 py-6 justify-center">
                <div className="w-4 h-4 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                <span className="text-xs text-muted-foreground">Querying Planespotters, adsbdb, OpenSky...</span>
              </div>
            )}

            {!aircraftIntelLoading && aircraftIntel && (
              <div className="space-y-4">
                {/* Photos */}
                {aircraftIntel.photos?.length > 0 && (
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Photos</p>
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {aircraftIntel.photos.map((ph: any, i: number) => (
                        <a key={i} href={ph.full_src || '#'} target="_blank" rel="noopener noreferrer" className="shrink-0">
                          {ph.thumbnail_src && (
                            <img src={ph.thumbnail_src} alt={`Photo ${i + 1}`}
                              className="h-24 w-auto rounded border border-border hover:ring-2 hover:ring-cyan-500 transition-all object-cover" />
                          )}
                          {ph.photographer && <p className="text-[9px] text-muted-foreground mt-0.5 text-center truncate w-24">{ph.photographer}</p>}
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Profile */}
                {(aircraftIntel.registration || aircraftIntel.operator) && (
                  <div className="rounded-lg border border-border p-3 space-y-1.5">
                    {[
                      ['Registration', aircraftIntel.registration],
                      ['Type', aircraftIntel.type],
                      ['ICAO Type', aircraftIntel.icao_type],
                      ['Manufacturer', aircraftIntel.manufacturer],
                      ['Operator', aircraftIntel.operator],
                      ['Country', aircraftIntel.operator_country],
                    ].filter(([, v]) => v).map(([label, value]) => (
                      <div key={label as string} className="flex justify-between text-[11px]">
                        <span className="text-muted-foreground">{label}</span>
                        <span className="text-foreground font-medium truncate max-w-[160px]">{value}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Flight history */}
                {aircraftIntel.recent_flights?.length > 0 && (
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Recent Flights (7 days)</p>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                      {aircraftIntel.recent_flights.map((f: any, i: number) => (
                        <div key={i} className="flex items-center gap-2 p-1.5 bg-muted/30 rounded text-[11px]">
                          <span className="font-mono text-cyan-400 w-16 shrink-0">{f.callsign || '—'}</span>
                          <span className="text-muted-foreground flex-1">{f.departure_airport || '?'} → {f.arrival_airport || '?'}</span>
                          <span className="text-muted-foreground shrink-0">{f.first_seen ? new Date(f.first_seen * 1000).toLocaleDateString() : ''}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {aircraftIntel.photos?.length === 0 && !aircraftIntel.registration && aircraftIntel.recent_flights?.length === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-4">No intelligence data found for this aircraft.</p>
                )}
              </div>
            )}

            {!aircraftIntelLoading && !aircraftIntel && (
              <div className="text-center py-8 space-y-2">
                <p className="text-xs text-muted-foreground">No ICAO24 identifier available.</p>
                <p className="text-[10px] text-muted-foreground/70">
                  This aircraft was detected via satellite imagery. ADS-B intelligence (registration, photos, flight history) requires a live transponder ICAO24 code, which is not available from imagery alone.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action bar */}
      <div className="p-3 border-t border-border flex gap-2 shrink-0">
        {onGeneratePackage && (
          <button
            onClick={() => onGeneratePackage(entity.entity_id)}
            className="flex-1 px-3 py-2 text-xs font-medium rounded bg-emerald-600 hover:bg-emerald-700 text-white transition-colors"
          >
            Generate Target Package
          </button>
        )}
        {onCreateTasking && entity.entity_type === 'LOCATION' && (
          <button
            onClick={() => onCreateTasking(entity.entity_id)}
            className="flex-1 px-3 py-2 text-xs font-medium rounded bg-blue-600 hover:bg-blue-700 text-white transition-colors"
          >
            Create Tasking
          </button>
        )}
      </div>
    </div>
  );
};

export default EntityDetailDrawer;
